import { icons as lucideIcons, X } from 'lucide-react';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { getBackgroundStyle, getBorderStyle } from '@/utils/colors';
import React from 'react';
import { useWebinarT } from '@/hooks/useWebinarT';

function iconNameToPascal(name: string): string {
  return name
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('');
}

interface PopupBuyerCardProps {
  message: string;
  icon?: string;
  showIcon?: boolean;
  visualIdentity?: WorkspaceVisualIdentity;
  onClose?: () => void;
  offerColors?: {
    backgroundColor: string;
    borderColor: string;
    textColor: string;
    iconColor: string;
    iconBackgroundColor: string;
  };
}

export function LucideLazyIcon({
  name,
  ...rest
}: { name: string } & React.SVGProps<SVGSVGElement>) {
  if (!name || typeof name !== 'string') return null;
  const pascalName = iconNameToPascal(name);
  const IconComponent = (
    lucideIcons as Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>>
  )[pascalName];
  if (!IconComponent) return null;
  return <IconComponent {...rest} />;
}

export default function PopupBuyerCard({
  message,
  icon = 'award',
  showIcon = true,
  visualIdentity,
  onClose,
  offerColors,
}: PopupBuyerCardProps) {
  const { t } = useWebinarT('common');
  const backgroundColorStyle = offerColors
    ? getBackgroundStyle(offerColors.backgroundColor ?? 'accent', undefined, visualIdentity)
    : visualIdentity?.theme === 'LIGHT'
      ? { backgroundColor: visualIdentity?.backgroundColor || '#FFFFFF' }
      : { backgroundColor: visualIdentity?.backgroundColor || '#1F1F1F' };

  const borderStyle = offerColors
    ? getBorderStyle(offerColors.borderColor ?? 'accent', undefined, visualIdentity)
    : {};

  const textColor =
    offerColors?.textColor || (visualIdentity?.theme === 'LIGHT' ? '#000000' : '#FFFFFF');
  const iconColor = offerColors?.iconColor || visualIdentity?.contrastColor || '#000000';
  const iconBackgroundColor = offerColors?.iconBackgroundColor
    ? getBackgroundStyle(offerColors.iconBackgroundColor ?? 'accent', undefined, visualIdentity)
    : { backgroundColor: visualIdentity?.accentColor || '#2C6CFF' };

  const hasBorder =
    offerColors?.borderColor &&
    offerColors.borderColor !== 'no-border' &&
    offerColors.borderColor !== 'transparent';

  return (
    <div
      className={`flex items-start justify-between mx-auto gap-2 p-4 rounded-[20px] w-full max-w-[408px] ${
        hasBorder ? 'border' : ''
      }`}
      style={{
        ...backgroundColorStyle,
        ...borderStyle,
      }}
    >
      {showIcon && (
        <div className="p-2 rounded-lg shrink-0" style={iconBackgroundColor}>
          <LucideLazyIcon name={icon || ''} width={24} height={24} color={iconColor || '#2C6CFF'} />
        </div>
      )}

      <div className="flex-1">
        <p className="text-base" style={{ color: textColor }}>
          {message}
        </p>
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 cursor-pointer"
          aria-label={t('close')}
        >
          <X className="size-4" style={{ color: textColor }} />
        </button>
      )}
    </div>
  );
}
